import { Box, Checkbox, FormControl, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { ExtraSmallButton } from '../custom';
import { StyledMenuItem, StyledSelect } from './styles';
import { actionTypes, EqualityOperator, expiryTypes, optionTypes, strategyCriteriaList } from './StrategyTemplate.types';
import { OptionType, SecurityCategory_CategoryCode } from '../../proto/generated/security';
import ExtraSmallTextField from '../common/ExtraSmallTextField';
import { AddRounded, RemoveRounded } from '@mui/icons-material';
import BinIcon from '../../assets/icons/BinIcon';
import { StrategyTemplateLegProto, StrategyTemplateLegProto_ExpirySelectionProto_ExpirySelectionType, StrategyTemplateLegProto_StrikeCriteriaProto_ReferenceType, StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType } from '../../proto/generated/strategy';
import { TransactionType } from '../../proto/generated/order';
import { selectPrimarySecurity } from '../../selectors/home';
import { useSelector } from 'react-redux';
import { selectUnderlyingOptionsTradeData } from '../../selectors/options';
import { v4 as uuid } from 'uuid';
import { ExtraSmallButtonAutomation } from '../automationQueryBuilder/styles';
import Add from '../../assets/icons/Add';

type Props = {
    strategyLegs: StrategyTemplateLegProto[] | undefined
    setStrategyLegs: Dispatch<SetStateAction<StrategyTemplateLegProto[]>>
    templateDecription?: string
    setTemplateDescription?: Dispatch<SetStateAction<string>>
    templateName?: string
    setTemplateName?: Dispatch<SetStateAction<string>>
    templateNameError?: boolean
    setTemplateNameError?: Dispatch<SetStateAction<boolean>>
    viewOnly?: boolean
}

const StrategyLegsRenderer = ({
    strategyLegs,
    setStrategyLegs,
    templateDecription,
    setTemplateDescription,
    templateName,
    setTemplateName,
    templateNameError,
    setTemplateNameError,
    viewOnly
}: Props) => {
    const { key } = useSelector(selectPrimarySecurity)
    const [errorIndices, setErrorIndices] = useState<Set<number>>(new Set());
    const [strategyCriteria, setStrategyCriteria] = useState(strategyCriteriaList()[0].key)
    const [strategyValue, setStrategyValue] = useState(0)
    const [closestPremiumParam, setClosestPremiumParam] = useState(EqualityOperator['~'])
    const [rollOverToNextExpiry, setRollOverToNextExpiry] = useState(true)
    const [deleteLegError, setDeleteLegError] = useState({
        error: '',
        index: -1
    })

    useEffect(() => {
        if (!strategyLegs?.length && strategyCriteria === StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.SAME_AS_PREVIOUS_LEG) {
            setStrategyCriteria(strategyCriteriaList()[0].key)
        }
    }, [strategyLegs?.length])

    useEffect(() => {
        if (strategyCriteria === StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.ATM_POINT) {
            setStrategyValue(0)
        } else if (strategyCriteria === StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.ATM_PERCENTAGE) {
            setStrategyValue(0)
        } else if (strategyCriteria === StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.STRADDLE_WIDTH) {
            setStrategyValue(0)
        } else if (strategyCriteria === StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.CLOSEST_PREMIUM) {
            setStrategyValue(50)
        } else if (strategyCriteria === StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.CP_ON_STRADDLE_PREMIUM) {
            setStrategyValue(5)
        } else if (strategyCriteria === StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.STANDARD_DEVIATION) {
            setStrategyValue(0)
        } else if (strategyCriteria === StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.CLOSEST_DELTA) {
            setStrategyValue(0.5)
        }
    }, [strategyCriteria])

    const ifAnyWeeklyLeg = useMemo(() => {
        return strategyLegs.some((leg) => leg.expirySelection?.expirySelectionType ? [StrategyTemplateLegProto_ExpirySelectionProto_ExpirySelectionType.WEEKLY, StrategyTemplateLegProto_ExpirySelectionProto_ExpirySelectionType.NEXT_WEEKLY].includes(leg.expirySelection.expirySelectionType) : false)
    }, [strategyLegs])

    const isCPSPCD = useMemo(() => {
        return [StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.CLOSEST_DELTA, StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.CLOSEST_PREMIUM, StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.CP_ON_STRADDLE_PREMIUM].includes(strategyCriteria)
    }, [strategyCriteria])

    const showRolloverExpiry = useMemo(() => strategyLegs.some(
        (strategyLeg) =>
            strategyLeg.categoryCode === SecurityCategory_CategoryCode.OPTIONS &&
            ifAnyWeeklyLeg &&
            strategyLeg.expirySelection?.expirySelectionType &&
            [
                StrategyTemplateLegProto_ExpirySelectionProto_ExpirySelectionType.MONTHLY,
                StrategyTemplateLegProto_ExpirySelectionProto_ExpirySelectionType.NEXT_MONTHLY
            ].includes(strategyLeg.expirySelection.expirySelectionType)
    ), [ifAnyWeeklyLeg, strategyLegs])

    const showMinDays = useMemo(() => strategyLegs.some(
        (strategyLeg) =>
            strategyLeg.expirySelection?.expirySelectionType ===
            StrategyTemplateLegProto_ExpirySelectionProto_ExpirySelectionType.MINIMUM_DAYS_TO_MONTHLY_EXPIRY
    ), [strategyLegs])

    const handleDeleteLeg = useCallback((i: number) => {
        if (
            strategyLegs.length - 1 > i &&
            strategyLegs[i].categoryCode === SecurityCategory_CategoryCode.OPTIONS &&
            strategyLegs[i].strikeCriteria?.strikeType !== StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.SAME_AS_PREVIOUS_LEG &&
            strategyLegs[i + 1].strikeCriteria?.strikeType === StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.SAME_AS_PREVIOUS_LEG
        ) {
            setDeleteLegError({
                error: "You cannnot delete this leg as it's connected to next Leg",
                index: i
            })
            setTimeout(() => {
                setDeleteLegError({
                    error: '',
                    index: -1
                })
            }, 4000)
        } else {
            const newStrategyLegs = JSON.parse(JSON.stringify(strategyLegs)) as StrategyTemplateLegProto[]
            newStrategyLegs.splice(i, 1)
            setStrategyLegs(newStrategyLegs)
        }
    }, [setStrategyLegs, strategyLegs])

    const toggleBuySell = useCallback((i: number) => {
        const newStrategyLegs = JSON.parse(JSON.stringify(strategyLegs)) as StrategyTemplateLegProto[]
        if (newStrategyLegs[i].transactionType === TransactionType.BUY) {
            newStrategyLegs[i].transactionType = TransactionType.SELL
        } else {
            newStrategyLegs[i].transactionType = TransactionType.BUY
        }
        setStrategyLegs(newStrategyLegs)
        setErrorIndices((prev) => {
            const newErrors = new Set(prev);
            newErrors.delete(i);
            return newErrors;
        });

    }, [setStrategyLegs, strategyLegs])

    const handleStrikeTypeChange = useCallback((i: number, value: StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType) => {
        const newStrategyLegs = JSON.parse(JSON.stringify(strategyLegs)) as StrategyTemplateLegProto[]
        newStrategyLegs[i].strikeCriteria = {
            ...newStrategyLegs[i].strikeCriteria,
            strikeType: value
        } as any
        setStrategyLegs(newStrategyLegs)
        setErrorIndices((prev) => {
            const newErrors = new Set(prev);
            newErrors.delete(i);
            return newErrors;
        });
    }, [setStrategyLegs, strategyLegs])

    const toggleOptionType = useCallback((i: number) => {
        const newStrategyLegs = JSON.parse(JSON.stringify(strategyLegs)) as StrategyTemplateLegProto[]
        if (newStrategyLegs[i].optionType === OptionType.CE) {
            newStrategyLegs[i].optionType = OptionType.PE
        } else {
            newStrategyLegs[i].optionType = OptionType.CE
        }
        setStrategyLegs(newStrategyLegs)
        setErrorIndices((prev) => {
            const newErrors = new Set(prev);
            newErrors.delete(i);
            return newErrors;
        });
    }, [setStrategyLegs, strategyLegs])

    const handleLotSizeChange = useCallback((value: number, i: number) => {
        const newStrategyLegs = JSON.parse(JSON.stringify(strategyLegs)) as StrategyTemplateLegProto[]
        if (value <= 0) {
            newStrategyLegs[i].lots = 1
        } else {
            newStrategyLegs[i].lots = value
        }
        setStrategyLegs(newStrategyLegs)
        setErrorIndices((prev) => {
            const newErrors = new Set(prev);
            newErrors.delete(i);
            return newErrors;
        });
    }, [setStrategyLegs, strategyLegs])

    const handleMinDaysChange = useCallback((i: number, value: number) => {
        const newStrategyLegs = JSON.parse(JSON.stringify(strategyLegs)) as StrategyTemplateLegProto[]
        newStrategyLegs[i].expirySelection = {
            expirySelectionType: StrategyTemplateLegProto_ExpirySelectionProto_ExpirySelectionType.MINIMUM_DAYS_TO_MONTHLY_EXPIRY,
            minimumDaysToExpiry: value
        }
        setStrategyLegs(newStrategyLegs)
        setErrorIndices((prev) => {
            const newErrors = new Set(prev);
            newErrors.delete(i);
            return newErrors;
        });
    }, [setStrategyLegs, strategyLegs])

    const handleParameterChange = useCallback((value: EqualityOperator, i: number) => {
        const newStrategyLegs = JSON.parse(JSON.stringify(strategyLegs)) as StrategyTemplateLegProto[]
        newStrategyLegs[i].strikeCriteria = {
            ...newStrategyLegs[i].strikeCriteria,
            parametersMap: {
                operator: value
            }
        } as any
        setStrategyLegs(newStrategyLegs)
        setErrorIndices((prev) => {
            const newErrors = new Set(prev);
            newErrors.delete(i);
            return newErrors;
        });
    }, [setStrategyLegs, strategyLegs])

    const handleStrategyValueChange = useCallback((e: any, i: number) => {
        const newStrategyLegs = JSON.parse(JSON.stringify(strategyLegs)) as StrategyTemplateLegProto[]
        newStrategyLegs[i].strikeCriteria = {
            ...newStrategyLegs[i].strikeCriteria,
            value: Number(e.target.value)
        } as any
        setStrategyLegs(newStrategyLegs)
        setErrorIndices((prev) => {
            const newErrors = new Set(prev);
            newErrors.delete(i);
            return newErrors;
        });
    }, [setStrategyLegs, strategyLegs])

    const handleExpiryChange = useCallback((i: number, value: StrategyTemplateLegProto_ExpirySelectionProto_ExpirySelectionType) => {
        const newStrategyLegs = JSON.parse(JSON.stringify(strategyLegs)) as StrategyTemplateLegProto[]
        newStrategyLegs[i].expirySelection = {
            expirySelectionType: value
        }
        setStrategyLegs(newStrategyLegs)
        setErrorIndices((prev) => {
            const newErrors = new Set(prev);
            newErrors.delete(i);
            return newErrors;
        });
    }, [setStrategyLegs, strategyLegs])

    const handleRollOverFlagChange = useCallback((i: number, value: boolean) => {
        const newStrategyLegs = JSON.parse(JSON.stringify(strategyLegs)) as StrategyTemplateLegProto[]
        newStrategyLegs[i].expirySelection = {
            expirySelectionType: newStrategyLegs[i].expirySelection?.expirySelectionType ?? StrategyTemplateLegProto_ExpirySelectionProto_ExpirySelectionType.MONTHLY,
            rolloverFlag: value
        }
        setStrategyLegs(newStrategyLegs)
        setErrorIndices((prev) => {
            const newErrors = new Set(prev);
            newErrors.delete(i);
            return newErrors;
        });
    }, [setStrategyLegs, strategyLegs])

    const onAddPositions = useCallback(() => {
        if (strategyLegs.length < 8) {
            const newStrategyLegs = JSON.parse(JSON.stringify(strategyLegs)) as StrategyTemplateLegProto[]
            let strategyLeg: StrategyTemplateLegProto = {
                id: uuid(),
                categoryCode: SecurityCategory_CategoryCode.OPTIONS,
                expirySelection: {
                    expirySelectionType: expiryTypes()[0].key
                },
                transactionType: actionTypes[0].key,
                lots: 1
            }
            if (expiryTypes()[0].key === StrategyTemplateLegProto_ExpirySelectionProto_ExpirySelectionType.MINIMUM_DAYS_TO_MONTHLY_EXPIRY && strategyLeg.expirySelection) {
                strategyLeg.expirySelection.minimumDaysToExpiry = 0
            }
            if (strategyLeg.expirySelection && ifAnyWeeklyLeg && [StrategyTemplateLegProto_ExpirySelectionProto_ExpirySelectionType.MONTHLY, StrategyTemplateLegProto_ExpirySelectionProto_ExpirySelectionType.NEXT_MONTHLY].includes(expiryTypes()[0].key)) {
                strategyLeg.expirySelection.rolloverFlag = rollOverToNextExpiry
            }
            strategyLeg = {
                ...strategyLeg,
                optionType: optionTypes[0].key,
                strikeCriteria: {
                    strikeType: strategyCriteria,
                    referenceType: StrategyTemplateLegProto_StrikeCriteriaProto_ReferenceType.SPOT,
                    value: strategyValue,
                    parametersMap: isCPSPCD ? {
                        operator: closestPremiumParam
                    } : {}
                }
            }
            newStrategyLegs.push(strategyLeg)
            setStrategyLegs(newStrategyLegs)
            setRollOverToNextExpiry(true)
        }
    }, [strategyLegs, ifAnyWeeklyLeg, strategyCriteria, strategyValue, isCPSPCD, closestPremiumParam, setStrategyLegs, rollOverToNextExpiry])

    const RenderAtmSelectionDropDown = useCallback((i = 0) => {
        const strategyCriteriaRender = strategyLegs[i].strikeCriteria?.strikeType
        const optionType = strategyLegs[i].optionType
        const CPCDtext = strategyCriteriaRender === StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.CLOSEST_DELTA ? 'CD' : 'CP'
        return (
            <FormControl sx={{ m: 0 }} variant="standard">
                {
                    (strategyCriteriaRender === StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.CLOSEST_PREMIUM || strategyCriteriaRender === StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.CP_ON_STRADDLE_PREMIUM || strategyCriteriaRender === StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.CLOSEST_DELTA) ?
                        <Box display="flex" alignItems="center" borderRadius={1} p={0}>
                            <FormControl variant="outlined" error={errorIndices.has(i)}>
                                <StyledSelect
                                    MenuProps={{ sx: { zIndex: 13000 } }}
                                    disabled={viewOnly}
                                    value={(strategyLegs[i].strikeCriteria?.parametersMap?.operator ?? EqualityOperator['~'])}
                                    sx={{ width: showRolloverExpiry && showMinDays ? '60px' : '100px', height: "24px", mr: showRolloverExpiry && showMinDays ? '10px' : "20px" }}
                                    onChange={(e) => handleParameterChange(Number(e.target.value), i)}
                                >
                                    <StyledMenuItem value={EqualityOperator['~']}>{`${CPCDtext}~`}</StyledMenuItem>
                                    <StyledMenuItem value={EqualityOperator['>=']}>{`${CPCDtext}>=`}</StyledMenuItem>
                                    <StyledMenuItem value={EqualityOperator['<=']}>{`${CPCDtext}<=`}</StyledMenuItem>
                                </StyledSelect>
                            </FormControl>

                            {
                                (
                                    strategyCriteriaRender === StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.CLOSEST_PREMIUM ||
                                    strategyCriteriaRender === StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.CLOSEST_DELTA
                                ) ?
                                    <>
                                        <TextField
                                            value={strategyLegs[i].strikeCriteria?.value}
                                            onChange={(e) => handleStrategyValueChange(e, i)}
                                            variant="outlined"
                                            placeholder="Type here"
                                            type='number'
                                            inputProps={(strategyCriteriaRender === StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.CLOSEST_DELTA ? {
                                                min: 0.01,
                                                max: 0.99,
                                                step: 0.01,
                                                sx: {
                                                    fontSize: '12px',
                                                    height: '24px',
                                                    padding: '5px 10px'
                                                }
                                            } : {
                                                sx: {
                                                    fontSize: '12px',
                                                    height: '24px',
                                                    padding: '5px 10px'
                                                }
                                            })
                                            }
                                            disabled={viewOnly}
                                            sx={{
                                                width: showRolloverExpiry && showMinDays ? '50px' : '60px',
                                                '& .MuiInputBase-root': {
                                                    fontSize: '12px',
                                                    height: '24px'
                                                },
                                                '& .Mui-disabled': {
                                                    color: theme => `${theme.palette.text.primary} !important`,
                                                    '-webkit-text-fill-color': theme => `${theme.palette.text.primary} !important`,
                                                }
                                            }}
                                        />
                                    </> :
                                    <>
                                        <FormControl error={errorIndices.has(i)}>
                                            <StyledSelect
                                                MenuProps={{ sx: { zIndex: 13000 } }}
                                                disabled={viewOnly}
                                                sx={{ width: showRolloverExpiry && showMinDays ? '50px' : '60px', height: "24px" }}
                                                variant='outlined'
                                                id="strike-price-label"
                                                value={strategyLegs[i].strikeCriteria?.value}
                                                onChange={(e) => handleStrategyValueChange(e, i)}
                                            >

                                                {
                                                    Array.from({ length: 12 }, (_, i) => i + 1).map(i => {
                                                        return <StyledMenuItem value={i * 5} key={i}>{Math.abs(i * 5)}% SP</StyledMenuItem>
                                                    })
                                                }
                                            </StyledSelect>
                                        </FormControl>
                                    </>
                            }
                        </Box> :
                        <FormControl
                            error={errorIndices.has(i)}
                            sx={{ minWidth: showRolloverExpiry && showMinDays ? "120px" : "180px" }}
                        >
                            <StyledSelect
                                MenuProps={{ 
                                    sx: { zIndex: 13000 },
                                    PaperProps: {
                                        sx: {
                                            maxHeight: 200,
                                            overflow: 'auto'
                                        }
                                    },
                                    anchorOrigin: {
                                        vertical: 'bottom',
                                        horizontal: 'left'
                                    },
                                    transformOrigin: {
                                        vertical: 'top',
                                        horizontal: 'left'
                                    }
                                }}
                                disabled={viewOnly}
                                sx={{ minWidth: showRolloverExpiry && showMinDays ? "120px" : "180px", height: "24px", textAlign: "center" }}
                                id="strike-price-label"
                                variant='outlined'
                                value={strategyLegs[i].strikeCriteria?.value}
                                onChange={(e) => handleStrategyValueChange(e, i)}
                            >
                                {
                                    strategyCriteriaRender === StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.ATM_POINT ?
                                        [
                                            ...Array.from({ length: 20 }, (_, i) => i - 20).map(i => (
                                                <StyledMenuItem value={i} key={`neg-${i}`}>
                                                    {optionType === OptionType.PE ? 'OTM' : 'ITM'} {Math.abs(i)}
                                                </StyledMenuItem>
                                            )),
                                            <StyledMenuItem value={0} key="atm">ATM</StyledMenuItem>,
                                            ...Array.from({ length: 20 }, (_, i) => i + 1).map(i => (
                                                <StyledMenuItem value={i} key={`pos-${i}`}>
                                                    {optionType === OptionType.CE ? 'OTM' : 'ITM'} {Math.abs(i)}
                                                </StyledMenuItem>
                                            ))
                                        ] :
                                        strategyCriteriaRender === StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.ATM_PERCENTAGE ?
                                            [
                                                Array.from({ length: 40 }, (_, i) => i - 40).map(i => {
                                                    return <StyledMenuItem value={i * 0.25} key={i}>ATM-{Math.abs(i * 0.25)}%</StyledMenuItem>
                                                }),
                                                <StyledMenuItem value={0} key={i}>ATM</StyledMenuItem>,
                                                Array.from({ length: 40 }, (_, i) => i + 1).map(i => {
                                                    return <StyledMenuItem value={i * 0.25} key={i}>ATM+{Math.abs(i * 0.25)}%</StyledMenuItem>
                                                })
                                            ] :
                                            strategyCriteriaRender === StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.STRADDLE_WIDTH ?
                                                [
                                                    Array.from({ length: 40 }, (_, i) => i - 40).map(i => {
                                                        return <StyledMenuItem value={i * 0.25} key={i}>ATM-{Math.abs(i * 0.25)}*SP</StyledMenuItem>
                                                    }),
                                                    <StyledMenuItem value={0} key={i}>ATM</StyledMenuItem>,
                                                    Array.from({ length: 40 }, (_, i) => i + 1).map(i => {
                                                        return <StyledMenuItem value={i * 0.25} key={i}>ATM+{Math.abs(i * 0.25)}*SP</StyledMenuItem>
                                                    })
                                                ] :
                                                strategyCriteriaRender === StrategyTemplateLegProto_StrikeCriteriaProto_StrikeType.STANDARD_DEVIATION ?
                                                    [
                                                        Array.from({ length: 40 }, (_, i) => i - 40).map(i => {
                                                            return <StyledMenuItem value={i * 0.25} key={i}>ATM-{Math.abs(i * 0.25)}*SD</StyledMenuItem>
                                                        }),
                                                        <StyledMenuItem value={0} key={i}>ATM</StyledMenuItem>,
                                                        Array.from({ length: 40 }, (_, i) => i + 1).map(i => {
                                                            return <StyledMenuItem value={i * 0.25} key={i}>ATM+{Math.abs(i * 0.25)}*SD</StyledMenuItem>
                                                        })
                                                    ] : null
                                }
                            </StyledSelect>
                        </FormControl>
                }
            </FormControl>
        )
    }, [strategyLegs, errorIndices, viewOnly, showRolloverExpiry, showMinDays, handleParameterChange, handleStrategyValueChange])

    return (
        <>
            {
                // (strategyLegs.length === 0) ? null :
                    <Stack direction={'column'} spacing={2} alignItems={'flex-start'}>
                        {
                            !viewOnly ?
                                <Stack direction={'row'} spacing={1}>
                                    {
                                        setTemplateName ?
                                            <FormControl sx={{ flex: 2 }}>
                                                <Typography variant="caption">Template Name</Typography>
                                                <TextField
                                                    disabled={viewOnly}
                                                    autoFocus
                                                    size={"small"}
                                                    aria-labelledby="strategy-template-name-label"
                                                    value={templateName}
                                                    onChange={(e) => {
                                                        setTemplateName(e.target.value);
                                                        if (e.target.value && setTemplateNameError) {
                                                            setTemplateNameError(false);
                                                        }
                                                    }}
                                                    error={templateNameError}
                                                    helperText={templateNameError ? "Name is mandatory" : ""}
                                                    variant="outlined"
                                                />

                                            </FormControl> : null
                                    }
                                    {
                                        setTemplateDescription ?
                                            <FormControl sx={{ flex: 3, fontSize: '12px' }}>
                                                <Typography variant="caption">Description</Typography>
                                                <TextField
                                                    disabled={viewOnly}
                                                    size={"small"}
                                                    aria-labelledby='strategy-template-description-lable'
                                                    value={templateDecription}
                                                    onChange={(e) => {
                                                        setTemplateDescription(e.target.value)
                                                    }}
                                                />
                                            </FormControl> : null
                                    }
                                </Stack> : null
                        }
                        {
                            strategyLegs.length !== 0 &&
                            <Table sx={{ minWidth: 500, width: 'auto' }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{
                                            fontSize: "12px",
                                            color: 'text.secondary',
                                            padding: 0,
                                            minHeight: "30px",
                                            width: showRolloverExpiry || showMinDays ? "35px" : "40px",
                                            borderBottom: "none"
                                        }}>B/S</TableCell>
                                        <TableCell sx={{
                                            fontSize: "12px",
                                            color: 'text.secondary',
                                            padding: 0,
                                            width: showRolloverExpiry && showMinDays ? "135px" : "200px",
                                            flexGrow: showRolloverExpiry || showMinDays ? 0 : 1,
                                            borderBottom: "none"
                                        }}>STRIKE CRITERIA</TableCell>
                                        <TableCell sx={{
                                            fontSize: "12px",
                                            color: 'text.secondary',
                                            padding: 0,
                                            width: showRolloverExpiry && showMinDays ? "135px" : "200px",
                                            flexGrow: showRolloverExpiry || showMinDays ? 0 : 1,
                                            borderBottom: "none"
                                        }}>STRIKE PRICE</TableCell>
                                        <TableCell sx={{
                                            fontSize: "12px",
                                            color: 'text.secondary',
                                            padding: 0,
                                            width: showRolloverExpiry || showMinDays ? "55px" : "60px",
                                            borderBottom: "none"
                                        }}>TYPE</TableCell>
                                        <TableCell sx={{
                                            fontSize: "12px",
                                            color: 'text.secondary',
                                            padding: 0,
                                            width: viewOnly ? '70px' : showRolloverExpiry || showMinDays ? '95px' : "100px",
                                            borderBottom: "none"
                                        }}>LOTS</TableCell>
                                        <TableCell sx={{
                                            fontSize: "12px",
                                            color: 'text.secondary',
                                            padding: 0,
                                            width: showRolloverExpiry || showMinDays ? '110px' : "110px",
                                            borderBottom: "none"
                                        }}>EXPIRY TYPE</TableCell>
                                        {showRolloverExpiry && showMinDays ? (
                                            <>
                                                <TableCell sx={{ fontSize: "12px", color: 'text.secondary', padding: 0, borderBottom: "none" }}>ROLLOVER</TableCell>
                                                <TableCell sx={{ fontSize: "12px", color: 'text.secondary', padding: 0, borderBottom: "none" }}>MIN. DAYS</TableCell>
                                            </>
                                        ) : showRolloverExpiry ? (
                                            <TableCell sx={{
                                                fontSize: "12px",
                                                color: 'text.secondary',
                                                padding: 0,
                                                width: showRolloverExpiry ? "110px" : "200px",
                                                borderBottom: "none"
                                            }}>ROLLOVER</TableCell>
                                        ) : showMinDays ? (
                                            <TableCell sx={{ fontSize: "12px", color: 'text.secondary', padding: 0, width: showMinDays ? "110px" : "200px", borderBottom: "none" }}>MIN.
                                                DAYS</TableCell>
                                        ) : null}

                                    </TableRow>
                                </TableHead>
                                <TableBody sx={{ '& .MuiTableRow-root': { height: '34px' } }}>
                                    {strategyLegs.map((strategyLeg, i) => (
                                        <TableRow key={i}>
                                            <TableCell sx={{ padding: 0, height: "28px", borderBottom: "none" }}>
                                                <ExtraSmallButton
                                                    className={`
                                                   ${strategyLeg.transactionType === TransactionType.BUY ? 'buy-button' : 'sell-button'}
                                                `}
                                                    style={errorIndices.has(i) ? { border: '1px solid #d32f2f' } : {}}
                                                    disabled={viewOnly}
                                                    onClick={() => toggleBuySell(i)}
                                                >
                                                    {strategyLeg.transactionType === TransactionType.BUY ? 'B' : 'S'}
                                                </ExtraSmallButton>

                                            </TableCell>
                                            <TableCell sx={{ padding: 0, borderBottom: "none" }}>
                                                {strategyLeg.categoryCode === SecurityCategory_CategoryCode.OPTIONS ? (
                                                    <FormControl
                                                        error={errorIndices.has(i)}
                                                        sx={{ minWidth: showRolloverExpiry && showMinDays ? "120px" : "180px" }}
                                                    >
                                                        <StyledSelect
                                                            MenuProps={{ sx: { zIndex: 13000 } }}
                                                            disabled={viewOnly}
                                                            value={strategyLeg.strikeCriteria?.strikeType}
                                                            onChange={(e) => handleStrikeTypeChange(i, Number(e.target.value))}
                                                            sx={{ height: '24px', textAlign: "center", minWidth: showRolloverExpiry && showMinDays ? "120px" : "180px" }}
                                                        >
                                                            {strategyCriteriaList(
                                                                (i > 0) && strategyLegs[i - 1].categoryCode === SecurityCategory_CategoryCode.OPTIONS
                                                            ).map((type) => (
                                                                <StyledMenuItem value={type.key} key={type.key}>
                                                                    {type.label}
                                                                </StyledMenuItem>
                                                            ))}
                                                        </StyledSelect>

                                                    </FormControl>
                                                ) : null}
                                            </TableCell>

                                            <TableCell sx={{ padding: 0, borderBottom: "none" }}>
                                                {RenderAtmSelectionDropDown(i)}
                                            </TableCell>
                                            <TableCell sx={{ padding: 0, borderBottom: "none" }}>
                                                {strategyLeg.categoryCode === SecurityCategory_CategoryCode.OPTIONS ? (
                                                    <FormControl error={errorIndices.has(i)}>
                                                        <ExtraSmallButton
                                                            disabled={viewOnly}
                                                            variant="outlined"
                                                            className="order-buttons"
                                                            style={{
                                                                height: '24px',
                                                                width: '32px',
                                                                padding: 0,
                                                                fontSize: "12px",
                                                                border: errorIndices.has(i) ? '1px solid #d32f2f' : undefined,
                                                                color: "inherit"

                                                            }}
                                                            onClick={() => toggleOptionType(i)}
                                                            data-event="C/P Changed"
                                                            data-event-source="Strategy Template"
                                                        >
                                                            {strategyLeg.optionType === OptionType.CE ? 'CE' : 'PE'}
                                                        </ExtraSmallButton>
                                                    </FormControl>
                                                ) : (
                                                    <Typography sx={{ textAlign: 'center', width: '32px', fontSize: "12px" }}>
                                                        FUT
                                                    </Typography>
                                                )}
                                            </TableCell>


                                            <TableCell sx={{ padding: 0, borderBottom: "none" }}>
                                                <ExtraSmallTextField
                                                    textFieldProps={{
                                                        disabled: viewOnly,
                                                        value: strategyLeg.lots,
                                                        className: 'rounded',
                                                        onChange: (e) => handleLotSizeChange(Number(e.target.value), i),
                                                        type: 'number',
                                                        inputProps: {
                                                            min: 0,
                                                            sx: {
                                                                maxWidth: 40
                                                            }
                                                        },
                                                        sx: {
                                                            '& .Mui-disabled': {
                                                                color: theme => `${theme.palette.text.primary} !important`,
                                                                '-webkit-text-fill-color': theme => `${theme.palette.text.primary} !important`,
                                                            }
                                                        },
                                                        error: errorIndices.has(i),
                                                        InputProps: {
                                                            startAdornment: viewOnly ? null : (
                                                                <IconButton onClick={() => handleLotSizeChange(strategyLeg.lots - 1, i)}
                                                                    disabled={strategyLeg.lots === 1 || viewOnly} size={'small'} sx={{ p: 0 }}>
                                                                    <RemoveRounded className="trade-panel-edit-icons" />
                                                                </IconButton>
                                                            ),
                                                            endAdornment: viewOnly ? null : (
                                                                <IconButton disabled={viewOnly} onClick={() => handleLotSizeChange(strategyLeg.lots + 1, i)}
                                                                    size={'small'} sx={{ p: 0 }}>
                                                                    <AddRounded className="trade-panel-edit-icons" />
                                                                </IconButton>
                                                            )
                                                        }
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ padding: 0, borderBottom: "none" }}>
                                                <FormControl
                                                    error={errorIndices.has(i)}
                                                    sx={{ minWidth: showRolloverExpiry && showMinDays ? "90px" : "90px" }}
                                                >
                                                    <StyledSelect
                                                        MenuProps={{ sx: { zIndex: 13000 } }}
                                                        disabled={viewOnly}
                                                        style={{ height: '24px', padding: 0, minWidth: showRolloverExpiry && showMinDays ? "90px" : "90px" }}
                                                        value={strategyLeg.expirySelection?.expirySelectionType}
                                                        onChange={(e) => handleExpiryChange(i, Number(e.target.value))}
                                                    >
                                                        {expiryTypes(strategyLeg.categoryCode === SecurityCategory_CategoryCode.FUTURES).map((type) => (
                                                            <StyledMenuItem value={type.key} key={type.key}>
                                                                {type.label}
                                                            </StyledMenuItem>
                                                        ))}
                                                    </StyledSelect>
                                                </FormControl>
                                            </TableCell>
                                            {showRolloverExpiry && (
                                                <TableCell sx={{ padding: 0, borderBottom: "none" }}>
                                                    {strategyLeg.categoryCode === SecurityCategory_CategoryCode.OPTIONS &&
                                                        ifAnyWeeklyLeg &&
                                                        strategyLeg.expirySelection?.expirySelectionType &&
                                                        [StrategyTemplateLegProto_ExpirySelectionProto_ExpirySelectionType.MONTHLY,
                                                        StrategyTemplateLegProto_ExpirySelectionProto_ExpirySelectionType.NEXT_MONTHLY].includes(strategyLeg.expirySelection.expirySelectionType) ? (
                                                        <FormControl error={errorIndices.has(i)}>
                                                            <Checkbox
                                                                disabled={viewOnly}
                                                                checked={strategyLeg.expirySelection?.rolloverFlag ?? true}
                                                                onChange={(e) => handleRollOverFlagChange(i, e.target.checked)}
                                                                sx={{
                                                                    padding: 0,
                                                                    width: '24px',
                                                                    '&.Mui-disabled': {
                                                                        color: theme => `${theme.palette.text.primary} !important`,
                                                                        '-webkit-text-fill-color': theme => `${theme.palette.text.primary} !important`,
                                                                    }
                                                                }}
                                                            />
                                                        </FormControl>
                                                    ) : null}
                                                </TableCell>
                                            )}
                                            {showMinDays && (
                                                <TableCell sx={{ padding: 0, borderBottom: "none" }}>
                                                    {(strategyLeg.expirySelection?.expirySelectionType === StrategyTemplateLegProto_ExpirySelectionProto_ExpirySelectionType.MINIMUM_DAYS_TO_MONTHLY_EXPIRY) ? (

                                                        <ExtraSmallTextField
                                                            textFieldProps={{
                                                                disabled: viewOnly,
                                                                value: strategyLeg.expirySelection?.minimumDaysToExpiry ?? 0,
                                                                onChange: (e) => {
                                                                    const val = Number(e.target.value);
                                                                    handleMinDaysChange(i, Math.min(45, Math.max(1, val)));
                                                                },
                                                                error: errorIndices.has(i),
                                                                type: 'number',
                                                                inputProps: {
                                                                    sx: {
                                                                        min: 1, max: 45,
                                                                        maxWidth: 40
                                                                    }
                                                                },
                                                                sx: {
                                                                    '& .Mui-disabled': {
                                                                        color: theme => `${theme.palette.text.primary} !important`,
                                                                        '-webkit-text-fill-color': theme => `${theme.palette.text.primary} !important`,
                                                                    }
                                                                },
                                                                InputProps: {
                                                                    startAdornment: viewOnly ? null : (
                                                                        <IconButton
                                                                            onClick={() => handleMinDaysChange(i, (strategyLeg.expirySelection?.minimumDaysToExpiry ?? 0) - 1)}
                                                                            disabled={(strategyLeg.expirySelection?.minimumDaysToExpiry ?? 0) === 1 || viewOnly}
                                                                            size={'small'} sx={{ p: 0 }}>
                                                                            <RemoveRounded className="trade-panel-edit-icons" />
                                                                        </IconButton>
                                                                    ),
                                                                    endAdornment: viewOnly ? null : (
                                                                        <IconButton
                                                                            onClick={() => handleMinDaysChange(i, (strategyLeg.expirySelection?.minimumDaysToExpiry ?? 0) + 1)}
                                                                            disabled={(strategyLeg.expirySelection?.minimumDaysToExpiry ?? 0) === 45 || viewOnly}
                                                                            size={'small'} sx={{ p: 0 }}>
                                                                            <AddRounded className="trade-panel-edit-icons" />
                                                                        </IconButton>
                                                                    )
                                                                }
                                                            }}
                                                        />
                                                    ) : null}
                                                </TableCell>
                                            )}
                                            {
                                                viewOnly ? null :
                                                    <TableCell sx={{ padding: 0, borderBottom: "none" }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%', maxWidth: '50px' }}>
                                                            {deleteLegError.index === i && (
                                                                <Typography fontSize={10} paddingRight={'8px'} color={'error'}>
                                                                    {deleteLegError.error}
                                                                </Typography>
                                                            )}
                                                            <IconButton sx={{ height: '36px' }} onClick={() => handleDeleteLeg(i)}>
                                                                <BinIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    </TableCell>
                                            }
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        }
                    </Stack>
            }
            {
                (viewOnly) ? null :
                    <Stack justifyContent="center" direction="row" mt={1}>
                        <ExtraSmallButtonAutomation
                            variant="outlined"
                            size="medium"
                            disabled={strategyLegs.length >= 8}
                            onClick={onAddPositions}
                            startIcon={<Add />}
                        >
                            Add Leg
                        </ExtraSmallButtonAutomation>
                    </Stack>
            }
        </>
    )
}

export default StrategyLegsRenderer